import React, { createContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { URLS } from "../configuration/urls";
import ECAxiosAPI, { AxiosAPI } from "./API";

const api_url: string = URLS.EC_SPOT_BE();
const keycloack_api_url: string = URLS.EC_AUTH_BE();

export interface EcAuthContextProps {
  isUserAuthenticated: boolean;
  isUserLogging: boolean;
  user: any;
  userStatusIndex: number;
  authUser: () => Promise<void>;
  getUserCompany: () => string;
  signin: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  userData: UserData;
  setRegisterInfo: React.Dispatch<React.SetStateAction<RegisterInfo>>;
  registerInfo: RegisterInfo;
  updateLang: (lang: string) => void;
  currentUserStatus: any;
  setCurrentUserStatus: (status: string) => void;
  isDirectCustomer: boolean;
  getRegistrationInfo: (email: string) => Promise<void>;
  ECAxiosAPI: any;
  isDrawerOpen: boolean;
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  checkUserData: () => void;
  getUserStatus: () => Promise<void>;
  getCompanyData: () => Promise<void>;
  resetUserData: () => void;
  isDarkMode: boolean;
  setIsDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
  getDarkMode: () => boolean;
  productStatusInfos: {
    productType: string;
    status: string;
  }[];
  tokenFromLocalStorage: () => void;
  setUserData: React.Dispatch<React.SetStateAction<UserData>>;
  IaUserId: string;
}

interface UserData {
  companyId: string | undefined;
  company: string;
  lang: string | null;
  userInfo:
    | {
        firstName: string;
        lastName: string;
        [key: string]: any;
      }
    | undefined;
  companyDetails: any;
  userShopDetails: any;
  roles: string[];
}

interface AuthProviderProps {
  children: React.ReactNode;
}

interface RegisterInfo {
  username: string;
  password: string;
}

const userDataInit: UserData = {
  lang: localStorage.getItem("lang") ? localStorage.getItem("lang") : "de"
} as UserData;

const EcAuthContext = createContext<EcAuthContextProps>(
  {} as EcAuthContextProps
);

interface ProductStatusInfosProps {
  productType: string;
  status: string;
}

export const EcAuthProvider = ({ children }: AuthProviderProps) => {
  const getDarkMode = () => {
    const darkModeEnabledEnv = [
      "localhost",
      "dev.eclear-solutions.com",
      "acc.eclear-solutions.com",
      "ref.eclear-solutions.com",
      "eclear-solutions.com"
    ];
    let darkModeEnabled = false;

    for (const keyword of darkModeEnabledEnv) {
      if (window.location.host.includes(keyword)) {
        darkModeEnabled = true;
        break;
      }
    }
    if (!darkModeEnabled) {
      localStorage.setItem("isDarkMode", "false");
      return false;
    } else {
      if (localStorage.getItem("isDarkMode") === null) {
        if (
          window.matchMedia &&
          window.matchMedia("(prefers-color-scheme: dark)").matches
        ) {
          localStorage.setItem("isDarkMode", "true");
          document.body.setAttribute("data-theme", "dark");
          return true;
        } else {
          localStorage.setItem("isDarkMode", "false");
          document.body.setAttribute("data-theme", "light");
          return false;
        }
      } else {
        document.body.setAttribute(
          "data-theme",
          JSON.parse(localStorage.getItem("isDarkMode") || "")
            ? "dark"
            : "light"
        );
        return JSON.parse(localStorage.getItem("isDarkMode") || "");
      }
    }
  };

  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [isUserLogging, setIsUserLogging] = useState(true);
  const [user, setUser] = React.useState({} as any);
  const [userStatusIndex, setUserStatusIndex] = React.useState(-1);
  const [userData, setUserData] = React.useState(userDataInit);
  const [registerInfo, setRegisterInfo] = React.useState({} as RegisterInfo);
  const [currentUserStatus, setCurrentUserStatus] = useState("");
  const [isDirectCustomer, setIsDirectCustomer] = useState(false);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(getDarkMode());
  const [productStatusInfos, setProductStatusInfos] = useState([]);

  const [IaUserId, setIaUserId] = useState("");

  const authUser = async (): Promise<void> => {
    console.log("Authenticating");
    try {
      const token = localStorage.getItem("getToken");

      if (!token || token === "undefined") {
        localStorage.removeItem("getToken");
        localStorage.removeItem("refreshToken");

        const apps = [
          "spot",
          "inventoryandassignment",
          "filevat",
          "checkvat",
          "clearvat"
        ];
        const matchingAppRoutes = apps.some((item) =>
          window.location.href.toLowerCase().includes(item)
        );
        if (matchingAppRoutes) {
          window.location.href = "/";
        }
        throw new Error("NotAuthorizedException");
      }
      const user: any = jwtDecode(token.replaceAll('"', ""));
      const userStatusValues = [
        "REGISTER",
        "COMPANY_INFO",
        "USER_INFO",
        "PAYMENT_METHOD_PENDING",
        "PAYMENT_METHOD_CONFIRMED",
        "DASHBOARD"
      ];
      const userStatusHeaders = {
        accept: "*/*",
        userId: user.sub
      };
      const { registrationStatus, productStatusInfos } = (await ECAxiosAPI.get(
        URLS.USER_STATUS(user.sub),
        {
          headers: userStatusHeaders
        }
      )) as any;
      setProductStatusInfos(productStatusInfos);
      const userStatusCode = userStatusValues.findIndex(
        (status: any) => status === registrationStatus
      );
      if (user) {
        setUser({
          accessToken: {
            payload: {
              sub: user?.sub,
              email: user?.email
            },
            jwtToken: token
          }
        });
        // const hasSpotSubscription = productStatusInfos.filter(
        //   (item: any) => item.productType === 'SPOT'
        // )
        const completedPayment = productStatusInfos.some(
          (item: ProductStatusInfosProps) => {
            return item.productType === "SPOT" && item.status === "COMPLETED";
          }
        );

        if (
          registrationStatus === "PAYMENT_METHOD_CONFIRMED" ||
          registrationStatus === "PAYMENT_METHOD_PENDING" ||
          registrationStatus === "COMPLETED"
          //  &&
          // productStatusInfos.length > 0 &&
          // completedPayment
        ) {
          setIsUserAuthenticated(true);
          setCurrentUserStatus(registrationStatus);
        } else {
          setUserStatusIndex(userStatusCode);
          registrationStatus === "COMPLETED" && !completedPayment
            ? setCurrentUserStatus("PAYMENT_METHOD_PENDING")
            : setCurrentUserStatus(registrationStatus);
          setIsUserAuthenticated(false);
        }
      } else {
        setIsUserAuthenticated(false);
      }

      setIsUserLogging(false);
    } catch (err) {
      // console.error('Signin failed: ', err)
      setIsUserAuthenticated(false);
      setIsUserLogging(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("redirect") || "";
    const userId = params.get("userId") || "";
    if (token !== "") {
      localStorage.setItem("getToken", token);
      authUser();
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (userId !== "") {
      setIaUserId(userId);
    }
  }, []);

  const getRegistrationInfo = async (email: String): Promise<void> => {
    console.log("getRegistrationInfo");
    const registrationLink: any = await ECAxiosAPI.get(
      `${api_url + URLS.REGISTRATION_LINK}?email=${email}`
    );
    // !registrationLink ||
    registrationLink?.refere === "jera"
      ? setIsDirectCustomer(true)
      : setIsDirectCustomer(false);
  };

  const getUserCompany = (): string => {
    // get Company name Value from subdomain from URL
    // COMPANY Values[spot, jera, dhl, uber, world]
    switch (true) {
      // case window.location.hostname.includes('jera'):
      //   return 'jera'
      case window.location.hostname.includes("spot"):
        return "spot";
      case window.location.hostname.includes("dhl"):
        return "dhl";
      case window.location.hostname.includes("worldline"):
        return "worldline";
      case window.location.hostname.includes("payone"):
        return "payone";
      default:
        return "spot";
    }
  };

  const getCompanyData = async (): Promise<void> => {
    console.log("getCompanyData");
    const companyReqHeaders = {
      userId: user.accessToken.payload.sub
    };
    return await ECAxiosAPI.get(URLS.GET_MERCHANT, {
      headers: companyReqHeaders
    })
      .then((res: any) => {
        return res;
      })
      .catch((err: any) => {
        return err;
      });
  };

  const tokenFromLocalStorage = () => {
    const token = localStorage.getItem("getToken");
    const user_token: any = token && jwtDecode(token.replaceAll('"', ""));
    return user_token;
  };

  const checkUserData = async (): Promise<void> => {
    console.log("checkUserData");
    const lang = localStorage.getItem("lang")
      ? localStorage.getItem("lang")
      : "de";
    if (Object.keys(user).length !== 0 || isUserAuthenticated) {
      try {
        const companyReqHeaders = {
          userId: user.accessToken.payload.sub
        };
        const company = (await ECAxiosAPI.get(URLS.GET_MERCHANT, {
          headers: companyReqHeaders
        })) as any;

        // const userInfo = (await ECAxiosAPI.get(
        //   URLS.GET_USER_BY_ID(company.merchantId)
        // )) as any

        const userShopDetails = (await ECAxiosAPI.get(
          // URLS.USER(user.accessToken.payload.sub)
          URLS.USER(tokenFromLocalStorage()?.sub)
        )) as any;

        const enabledSubs = userShopDetails.userSubscriptions
          .filter((item: any) => item.status.toLowerCase() === "enabled")
          .map((item: any) => {
            return item.productType;
          });

        const userGroupsList = userShopDetails.userGroups.map(
          (item: any) => item.name
        );
        setUserData({
          companyId: company.merchantId,
          company: getUserCompany(),
          userInfo: {
            ...userShopDetails,
            email: userShopDetails.email,
            firstName: userShopDetails.firstName,
            lastName: userShopDetails.lastName,
            tenantDetails: {
              email: userShopDetails.email,
              firstName: userShopDetails.firstName,
              lastName: userShopDetails.lastName
            },
            role: [...userGroupsList, ...enabledSubs]
          },
          lang,
          companyDetails: [{ ...company, vatID: company.vatId }],
          userShopDetails: userShopDetails,
          roles: [...userGroupsList, ...enabledSubs]
        });
      } catch (err) {
        console.error(err);
      }
    } else {
      setUserData({
        companyId: undefined,
        userInfo: undefined,
        company: getUserCompany(),
        lang,
        companyDetails: [],
        userShopDetails: {},
        roles: []
      });
    }
  };

  const signin = async (
    username: string,
    password: string
  ): Promise<boolean> => {
    setRegisterInfo({
      username,
      password
    });
    try {
      // const res = await Auth.signIn(username, password);
      const res: any = await AxiosAPI.post(keycloack_api_url + URLS.LOGIN, {
        username: username,
        password: password
      });
      localStorage.setItem("getToken", res?.data.access_token);
      localStorage.setItem("refreshToken", res?.data.refresh_token);
      setIsUserLogging(false);

      if (res) {
        setIsUserAuthenticated(true);
        return true;
      } else {
        return false;
      }
    } catch (err) {
      setCurrentUserStatus(err);
      setIsUserLogging(false);
      return err;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      localStorage.removeItem("getToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("settings");
      window.location.reload();
      window.location.href = "/";
    } catch (err) {
      console.error("signOut failed: ", err);
    }
  };

  const updateLang = (lang: string) => {
    localStorage.setItem("lang", lang);
    setUserData({ ...userData, lang });
  };

  const getUserStatus = async () => {
    console.log("getUserStatus");
    const token = localStorage.getItem("getToken");

    if (!token || token === "undefined") {
      localStorage.removeItem("getToken");
      localStorage.removeItem("refreshToken");
      throw new Error("NotAuthorizedException");
    }
    const user: any = jwtDecode(token.replaceAll('"', ""));
    const userStatusHeaders = {
      accept: "*/*",
      userId: user.sub
    };
    const { registrationStatus, productStatusInfos } = (await ECAxiosAPI.get(
      URLS.USER_STATUS(user.sub),
      {
        headers: userStatusHeaders
      }
    )) as any;
    setProductStatusInfos(productStatusInfos);
    return registrationStatus.registrationStatus;
  };

  useEffect(() => {
    authUser();
    getDarkMode();
  }, []);

  useEffect(() => {
    document.body.setAttribute("data-theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  useEffect(() => {
    Object.keys(user).length !== 0 || isUserAuthenticated === true
      ? checkUserData()
      : console.log();
  }, [user, isUserAuthenticated]);

  const resetUserData = () => {
    setIsUserAuthenticated(false);
    setUser({});
    setUserStatusIndex(-1);
    setCurrentUserStatus("");
    const language = localStorage.getItem("lang");
    localStorage.removeItem("getToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("settings");
    setUserData({
      companyId: "",
      company: "",
      userInfo: {
        firstName: "",
        lastName: ""
      },
      lang: language || "de",
      companyDetails: [],
      userShopDetails: {},
      roles: []
    });
  };

  return (
    <EcAuthContext.Provider
      value={{
        isUserAuthenticated,
        isUserLogging,
        user,
        userStatusIndex,
        authUser,
        getUserCompany,
        userData,
        registerInfo,
        signin,
        logout,
        updateLang,
        currentUserStatus,
        setCurrentUserStatus,
        isDirectCustomer,
        getRegistrationInfo,
        ECAxiosAPI,
        isDrawerOpen,
        setIsDrawerOpen,
        checkUserData,
        getUserStatus,
        getCompanyData,
        resetUserData,
        isDarkMode,
        setIsDarkMode,
        getDarkMode,
        setRegisterInfo,
        productStatusInfos,
        tokenFromLocalStorage,
        setUserData,
        IaUserId
      }}
    >
      {children}
    </EcAuthContext.Provider>
  );
};

export default EcAuthContext;
